// Third party
import React, { Component } from 'react';
import classNames from 'classnames';
import { SortingRule } from 'react-table';

// Components
import FundDetails from './FundDetails';
import { PrimaryButton, IconButton } from '../buttons';
import { IntegerValue } from '../common';
import { withErrorBoundary } from '../errors';
import { Tooltip } from '../tooltips';
import AdvancedSearchResult from './AdvancedSearchResult';
import ShelfStatus from '../shelf-management/ShelfStatus';

// Utils
import { formatMessage, FormattedMessage, formatNumber, formatLocalizedString } from '../../../utils/i18n';
import { getHoldingPerformanceVal } from '../../../utils/advanced-search';
import { uniqueArray } from '../../../utils/objects';
import { PillLabel } from '../misc';

// Types
import { FundSearchState } from '../../../redux/modules/types/fund-search';
import Fund from '@/types/fund';

const roundValue =
  (digits: number) =>
  ({ value }: any) => <span>{value ? formatNumber(value, digits) : '-'}</span>;

type Props = {
  fundState: FundSearchState;
  onSortingChange: (sorting: SortingRule[]) => void;
  setSelectedFunds: (funds: Fund[]) => void;
  setFocusedFund: (fund?: Fund) => void;
  loading?: boolean;
  onToggleSaved: (fund: Fund) => void;
  savedFunds: Fund[];
  insideModal?: boolean;
  onStartComparison: () => void;
  onAddToPortfolio: () => void;
  onLoadMoreResults: () => void;
  hasMore: boolean;
  totalResultsCount?: number;
  useWindowScrollAncestor?: boolean;
  compact?: boolean;
  canSelect?: boolean;
  hideSaveFund?: boolean;
  showInlineAdd?: boolean;
  onAddItem: (fund: Fund) => void;
  onArrowClick?: (fund: Fund) => void;
  overrideShowArrow?: boolean;
  customTableActions?: JSX.Element[];
  focusOnRowClick: boolean;
  onClearComplianceAlternatives: () => void;
  onCompareAlternatives?: () => void;
  addingToPortfolio: boolean;
  addingToComparison: boolean;
};

class FundSearchResult extends Component<Props> {
  static defaultProps = {
    onSortingChange: () => {},
    onToggleSaved: () => {},
    onAddItem: () => {},
    focusOnRowClick: true,
  };

  componentWillReceiveProps(nextProps) {
    // Reset the fund details when a new search is triggered
    if (nextProps.loading !== this.props.loading) {
      this.setFocusedFund(undefined);
    }
  }

  onAddItem = (fund: Fund) => {
    this.props.onAddItem(fund);
  };

  getDefaultColumns = () => {
    const { hideSaveFund, overrideShowArrow, showInlineAdd } = this.props;

    return [
      {
        accessor: '_id',
        includeInCompact: true,
        className: 'add-item',
        maxWidth: 36,
        sortable: false,
        show: !!showInlineAdd,
        Cell: ({ original }: { value: boolean; original: Fund }) => (
          <Tooltip message={formatMessage('Funds.AddToPortfolio')}>
            <IconButton className="add-icon" icon="plus" onClick={() => this.onAddItem(original)} />
          </Tooltip>
        ),
      },
      {
        Header: () => <span>{formatMessage('Funds.Table.Headers.FundName')}</span>,
        accessor: 'names.full',
        className: 'fund-name',
        minWidth: 220,
        includeInCompact: true,
        Cell: ({ original, value }: { original: Fund; value: string }) => (
          <>
            <Tooltip message={value}>
              <span>{value}</span>
            </Tooltip>
            {original.flags && <ShelfStatus statuses={original.flags} />}
          </>
        ),
      },
      {
        Header: () => <span>{formatMessage('Funds.Table.Headers.FundType')}</span>,
        accessor: 'universe',
        className: 'fund-type',
        minWidth: 105,
        Cell: ({ value }: { value: string }) => (
          <Tooltip message={value}>
            <PillLabel text={value} />
          </Tooltip>
        ),
      },
      {
        Header: () => <span>{formatMessage('Funds.Table.Headers.Classification')}</span>,
        accessor: 'classification',
        minWidth: 140,
        Cell: ({ value }: { value: string }) => (
          <Tooltip message={value}>
            <span>{value}</span>
          </Tooltip>
        ),
      },
      {
        Header: () => <span>{formatMessage('Funds.Table.Headers.1Y')}</span>,
        accessor: 'performance.percentGrowth.1Y',
        Cell: ({ value }: { value: number }) => <IntegerValue value={value} growth />,
        maxWidth: 80,
      },
      {
        Header: () => <span>{formatMessage('Funds.Table.Headers.3Y')}</span>,
        accessor: 'performance.percentGrowth.3Y',
        Cell: ({ value }: { value: number }) => <IntegerValue value={value} growth />,
        includeInCompact: true,
        maxWidth: 80,
      },
      {
        Header: () => <span>{formatMessage('Funds.Table.Headers.5Y')}</span>,
        accessor: 'performance.percentGrowth.5Y',
        Cell: ({ value }: { value: number }) => <IntegerValue value={value} growth />,
        maxWidth: 80,
      },
      {
        Header: () => <span>{formatMessage('Funds.Table.Headers.INC')}</span>,
        accessor: 'performance.percentGrowth.INC',
        Cell: ({ value }: { value: number }) => <IntegerValue value={value} growth />,
        includeInCompact: true,
        maxWidth: 80,
      },
      {
        Header: () => <span>{formatMessage('Funds.Table.Headers.MER')}</span>,
        accessor: 'mer',
        Cell: roundValue(2),
        includeInCompact: true,
        maxWidth: 80,
      },
      {
        Header: () => <span>{formatMessage('Funds.Table.Headers.Beta')}</span>,
        accessor: 'performance.beta',
        Cell: roundValue(2),
        includeInCompact: true,
        maxWidth: 80,
      },
      {
        Header: () => <span>{formatMessage('Funds.Table.Headers.StdDev')}</span>,
        accessor: 'performance.standardDeviation',
        Cell: roundValue(2),
        maxWidth: 80,
      },
      {
        accessor: '_id',
        className: 'cell-icon',
        sortable: false,
        width: 48,
        includeInCompact: true,
        show: !hideSaveFund,
        Cell: ({ original }: { original: Fund }) => {
          const classes = classNames('saved-fund-btn', {
            active: this.props.savedFunds && this.props.savedFunds.some(f => f._id === original._id),
          });
          return (
            <Tooltip message={formatMessage('Funds.AddToFavorites')}>
              <IconButton className={classes} icon="star-full" onClick={() => this.props.onToggleSaved(original)} />
            </Tooltip>
          );
        },
      },
      {
        accessor: 'isFocused',
        includeInCompact: true,
        className: 'focused-cell',
        width: 40,
        sortable: false,
        show: overrideShowArrow || !!this.props.fundState.focusedFund,
        Cell: ({ value, original }: { value: boolean; original: Fund }) => {
          if (!value && !overrideShowArrow) {
            return null;
          }

          return (
            <Tooltip message={formatMessage('Funds.ViewDetails')}>
              <IconButton className="focused-arrow" icon="cheveron-right" onClick={e => this.onArrowClick(e, original)} />
            </Tooltip>
          );
        },
      },
    ];
  };

  onArrowClick = (event: React.MouseEvent, original: Fund) => {
    const {
      fundState: { funds },
    } = this.props;

    if (this.props.onArrowClick === undefined || !funds) {
      return;
    }

    event.stopPropagation();

    // Get non formatted fund
    const item = funds.find(f => f._id === original._id);

    if (!item) {
      return;
    }

    this.props.onArrowClick(item);
  };

  formatData = (funds?: Fund[]) => {
    const {
      fundState: { focusedFund },
    } = this.props;

    if (!funds) {
      return [];
    }

    return funds.map(fund => ({
      ...fund,
      names: {
        full: formatLocalizedString(fund.names.full),
        short: formatLocalizedString(fund.names.short),
      },
      classification: formatLocalizedString(fund.classification),
      objective: formatLocalizedString(fund.objective),
      assetType: fund.assetType ? formatLocalizedString(fund.assetType) : '',
      universe: fund.universe ? formatLocalizedString(fund.universe) : '',
      isFocused: focusedFund && focusedFund._id === fund._id,
      performance: {
        ...fund.performance,
        beta: getHoldingPerformanceVal(fund, 'beta'),
        standardDeviation: getHoldingPerformanceVal(fund, 'standardDeviation'),
      },
    }));
  };

  getActions = () => {
    const { addingToPortfolio, addingToComparison, onAddToPortfolio, onStartComparison } = this.props;
    return [
      <PrimaryButton key="action-add" loading={addingToPortfolio} compact icon="plus" onClick={onAddToPortfolio}>
        <FormattedMessage id={`Search.Funds.${addingToPortfolio ? 'AddingToPortfolio' : 'AddToPortfolio'}`} />
      </PrimaryButton>,
      <PrimaryButton key="action-compare" loading={addingToComparison} compact icon="plus" onClick={onStartComparison}>
        <FormattedMessage id={`Search.Funds.${addingToComparison ? 'AddingToComparison' : 'AddToComparison'}`} />
      </PrimaryButton>,
    ];
  };

  hideDetails = () => {
    this.setFocusedFund(undefined);
  };

  onRowClick = (id: string) => {
    const {
      fundState: { funds, focusedFund },
    } = this.props;

    if (!funds) {
      return;
    }

    const item = funds.find(f => f._id === id);

    if (item) {
      if (focusedFund === item) {
        this.setFocusedFund(undefined);
      } else {
        this.setFocusedFund(item);
      }
    }
  };

  onCompareAlternatives = (id: string) => {
    const { onCompareAlternatives } = this.props;

    if (onCompareAlternatives) {
      onCompareAlternatives();
    }

    this.onRowClick(id);
  };

  setFocusedFund = (item?: Fund) => {
    this.props.setFocusedFund(item);

    if (item?._id !== this.props.fundState.focusedFund?._id) {
      this.props.onClearComplianceAlternatives();
    }
  };

  getFundDetails = () => {
    const {
      fundState: { focusedFund },
    } = this.props;

    return (
      focusedFund && (
        <FundDetails
          classicVersionOnly
          className="flex-grow-1"
          fund={focusedFund}
          onClose={this.hideDetails}
          onCompareAlternatives={this.onCompareAlternatives}
        />
      )
    );
  };

  onSelectItems = (ids: string[]) => {
    const {
      fundState: { funds, selectedFunds },
    } = this.props;

    if (!funds) {
      return;
    }

    const newFunds = funds.filter(f => ids.includes(f._id));
    const selected = uniqueArray([...selectedFunds, ...newFunds]);

    this.props.setSelectedFunds(selected);
  };

  onUnselectItems = (ids: string[]) => {
    const {
      fundState: { selectedFunds },
    } = this.props;

    const items = selectedFunds.filter(i => !ids.includes(i._id));

    this.props.setSelectedFunds(items);
  };

  render() {
    const {
      fundState: { selectedFunds, focusedFund, funds },
      insideModal,
      loading,
      totalResultsCount,
      useWindowScrollAncestor,
      hasMore,
      compact,
      canSelect,
      customTableActions,
      focusOnRowClick,
    } = this.props;

    return (
      <AdvancedSearchResult
        classicVersionOnly
        className="fund-search-result"
        data={funds && this.formatData(funds)}
        selected={this.formatData(selectedFunds)}
        columns={this.getDefaultColumns()}
        onSortingChange={this.props.onSortingChange}
        onRowClick={focusOnRowClick ? this.onRowClick : undefined}
        loading={loading}
        insideModal={insideModal}
        onLoadMoreResults={this.props.onLoadMoreResults}
        hasMore={hasMore}
        totalResultsCount={totalResultsCount}
        useWindowScrollAncestor={useWindowScrollAncestor}
        actions={this.getActions()}
        itemDetails={this.getFundDetails()}
        onSelectItems={this.onSelectItems}
        onUnselectItems={this.onUnselectItems}
        selectedTitle={formatMessage('Search.Funds.SelectedTitle', { count: selectedFunds.length })}
        focusedItem={focusedFund}
        labelAccessor="names.short"
        compact={compact}
        canSelect={canSelect}
        customTableActions={customTableActions}
      />
    );
  }
}

export default withErrorBoundary(FundSearchResult);
